import React, { useState, useEffect } from 'react';
import { FiEdit2, FiTrash2, FiCheck, FiX, FiPlus, FiChevronRight } from 'react-icons/fi';
import './addShipmentFee.css';

const AddShipmentFee = () => {
  // State management
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedPort, setSelectedPort] = useState('');
  const [baseCost, setBaseCost] = useState('');
  const [shippingAmount14To20m3, setShippingAmount14To20m3] = useState('');
  const [containerRate, setContainerRate] = useState('');
  const [containerRate40ft, setContainerRate40ft] = useState('');
  const [charges, setCharges] = useState([]);
  const [portsWithoutCharges, setPortsWithoutCharges] = useState([]);
  const [countries, setCountries] = useState([]);
  const [ports, setPorts] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editingFields, setEditingFields] = useState({});
  const [tempValues, setTempValues] = useState({});
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [expandedForm, setExpandedForm] = useState(false);
  const [minimizedSections, setMinimizedSections] = useState({
    charges: false,
    pending: false
  });
  const [searchCharges, setSearchCharges] = useState('');
  const [searchPending, setSearchPending] = useState('');

  const shippingOptions = ['CIF', 'CNF'];

  // Check if any cost field has a value
  const hasAnyCostValue = baseCost || shippingAmount14To20m3 || containerRate || containerRate40ft;

  // API calls
  useEffect(() => {
    fetchConfiguredPorts();
    fetchNotConfiguredPorts();
    fetchCountries();
  }, []);

  useEffect(() => {
    if (selectedCountry) {
      fetchPortsByCountry();
    }
  }, [selectedCountry]);

  const fetchConfiguredPorts = async () => {
    try {
      const response = await fetch('https://car.amar.co.jp/api/authorization/admin-shipment-panel/ports/configured', {
        headers: {
          'accept': 'application/json'
        }
      });
      const data = await response.json();
      
      const groupedByPort = {};
      data.forEach(item => {
        const key = `${item.countryName}__${item.portName}`;
        if (!groupedByPort[key]) {
          groupedByPort[key] = {
            portId: item.portId,
            portName: item.portName,
            countryName: item.countryName,
            countryId: item.countryId,
            shippingOptions: []
          };
        }
        groupedByPort[key].shippingOptions.push({
          type: item.shippingOptionType,
          baseCost: item.shippingAmount || 0,
          shippingAmount14To20m3: item.shippingAmount14To20m3 || 0,
          containerRate: item.containerRate || 0,
          containerRate40ft: item.containerRate40ft || 0,
          shippingOptionValueId: item.shippingOptionValueId
        });
      });

      setCharges(Object.values(groupedByPort));
    } catch (error) {
      console.error('Error fetching configured ports:', error);
    }
  };

  const fetchNotConfiguredPorts = async () => {
    try {
      const response = await fetch('https://car.amar.co.jp/api/authorization/admin-shipment-panel/ports/not-configured', {
        headers: {
          'accept': 'application/json'
        }
      });
      const data = await response.json();
      setPortsWithoutCharges(data);
    } catch (error) {
      console.error('Error fetching not configured ports:', error);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await fetch('https://car.amar.co.jp/api/authorization/shipping/countries', {
        headers: {
          'accept': 'application/json'
        }
      });
      const data = await response.json();
      setCountries(data);
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  const fetchPortsByCountry = async () => {
    const country = countries.find(c => c.countryName === selectedCountry);
    if (!country) return;

    try {
      const response = await fetch(`https://car.amar.co.jp/api/authorization/shipping/ports/country/${country.countryId}`, {
        headers: {
          'accept': 'application/json'
        }
      });
      const data = await response.json();
      setPorts(data);
    } catch (error) {
      console.error('Error fetching ports by country:', error);
    }
  };

  const handlePortWithoutChargeClick = (port) => {
    setSelectedCountry(port.countryName);
    setSelectedPort(port.portName);
    setExpandedForm(true);
    document.querySelector('.config-form-add-new-shipment').scrollIntoView({ behavior: 'smooth' });
  };

  const handleCountryChange = (e) => {
    setSelectedCountry(e.target.value);
    setSelectedPort('');
  };

  const handlePortChange = (e) => {
    setSelectedPort(e.target.value);
  };

  const handleSave = async () => {
    if (!selectedCountry || !selectedPort) {
      alert('Please select country and port');
      return;
    }

    if (!hasAnyCostValue) {
      alert('Please enter at least one cost value');
      return;
    }

    const country = countries.find(c => c.countryName === selectedCountry);
    const port = ports.find(p => p.portName === selectedPort);

    if (!country || !port) {
      alert('Invalid country or port selection');
      return;
    }

    try {
      const response = await fetch('https://car.amar.co.jp/api/authorization/admin-shipment-panel/shipping-option', {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          portId: port.portId,
          shippingOptionIds: [1, 2],
          shippingAmount: parseFloat(baseCost) || 0,
          shippingAmount14To20m3: parseFloat(shippingAmount14To20m3) || 0,
          containerRate: parseFloat(containerRate) || 0,
          containerRate40ft: parseFloat(containerRate40ft) || 0
        })
      });

      if (!response.ok) {
        if (response.status === 500) {
          setErrorMessage('Shipping option is already configured.');
        } else {
          setErrorMessage('Failed to save configuration');
        }
        setShowErrorDialog(true);
        return;
      }

      setSuccessMessage('Shipping options created successfully');
      setShowSuccessDialog(true);
      setTimeout(() => setShowSuccessDialog(false), 2000);
      fetchConfiguredPorts();
      fetchNotConfiguredPorts();
      resetForm();
    } catch (error) {
      console.error('Error saving configuration:', error);
      setErrorMessage('Failed to save configuration');
      setShowErrorDialog(true);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      const response = await fetch(`https://car.amar.co.jp/api/authorization/admin-shipment-panel/shipping-option/${deleteId}`, {
        method: 'DELETE',
        headers: {
          'accept': 'application/json'
        }
      });

      if (response.ok) {
        setSuccessMessage('Shipping option deleted successfully');
        setShowSuccessDialog(true);
        setTimeout(() => setShowSuccessDialog(false), 2000);
        fetchConfiguredPorts();
        fetchNotConfiguredPorts();
      } else {
        throw new Error('Failed to delete shipping option');
      }
    } catch (error) {
      console.error('Error deleting shipping option:', error);
      alert('Failed to delete shipping option');
    } finally {
      setShowDeleteConfirm(false);
      setDeleteId(null);
    }
  };

  const handleEditClick = (id, option) => {
    setEditingId(id);
    setEditingFields({});
    setTempValues({
      baseCost: option.baseCost,
      shippingAmount14To20m3: option.shippingAmount14To20m3,
      containerRate: option.containerRate,
      containerRate40ft: option.containerRate40ft
    });
  };

  const handleFieldChange = (field, value) => {
    setTempValues(prev => ({
      ...prev,
      [field]: value
    }));
    setEditingFields(prev => ({
      ...prev,
      [field]: true
    }));
  };

  const handleEditSave = async () => {
    if (!editingId || Object.keys(editingFields).length === 0) {
      return;
    }

    try {
      const requestBody = {
        shippingOptionValueId: editingId
      };

      if (editingFields.baseCost) {
        requestBody.shippingAmount = parseFloat(tempValues.baseCost);
      }
      if (editingFields.shippingAmount14To20m3) {
        requestBody.shippingAmount14To20M3 = parseFloat(tempValues.shippingAmount14To20m3);
      }
      if (editingFields.containerRate) {
        requestBody.containerRate = parseFloat(tempValues.containerRate);
      }
      if (editingFields.containerRate40ft) {
        requestBody.containerRate40Ft = parseFloat(tempValues.containerRate40ft);
      }

      const response = await fetch('https://car.amar.co.jp/api/authorization/admin-shipment-panel/shipping-option-amount-update', {
        method: 'PUT',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        setSuccessMessage('Shipping option updated successfully');
        setShowSuccessDialog(true);
        setTimeout(() => setShowSuccessDialog(false), 2000);
        fetchConfiguredPorts();
      } else {
        throw new Error('Failed to update shipping option');
      }
    } catch (error) {
      console.error('Error updating shipping option:', error);
      setErrorMessage('Failed to update shipping option');
      setShowErrorDialog(true);
    } finally {
      handleCancelEdit();
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingFields({});
    setTempValues({});
  };

  const resetForm = () => {
    setSelectedCountry('');
    setSelectedPort('');
    setBaseCost('');
    setShippingAmount14To20m3('');
    setContainerRate('');
    setContainerRate40ft('');
    setExpandedForm(false);
  };

  const confirmDelete = (id) => {
    setDeleteId(id);
    setShowDeleteConfirm(true);
  };

  const handleExpandToggle = () => {
    setExpandedForm(!expandedForm);
  };

  const toggleSectionMinimize = (section) => {
    setMinimizedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const filteredCharges = charges.filter(entry =>
    entry.portName.toLowerCase().includes(searchCharges.toLowerCase()) ||
    entry.countryName.toLowerCase().includes(searchCharges.toLowerCase())
  );

  const filteredPending = portsWithoutCharges.filter(port =>
    port.portName.toLowerCase().includes(searchPending.toLowerCase()) ||
    port.countryName.toLowerCase().includes(searchPending.toLowerCase())
  );

  useEffect(() => {
    if (searchCharges && minimizedSections.charges) {
      setMinimizedSections(prev => ({ ...prev, charges: false }));
    }
  }, [searchCharges, minimizedSections.charges]);

  useEffect(() => {
    if (searchPending && minimizedSections.pending) {
      setMinimizedSections(prev => ({ ...prev, pending: false }));
    }
  }, [searchPending, minimizedSections.pending]);

  return (
    <div className="shipment-container">
      <div className="main-header">
        <div className="header-title">Shipment Configuration</div>
        <div className="header-stats">
          <span className="stat-badge">{charges.length} Configured</span>
          <span className="stat-badge pending">{portsWithoutCharges.length} Pending</span>
        </div>
      </div>

      {showSuccessDialog && (
        <div className="success-dialog-add-shipment-fee">
          {successMessage}
        </div>
      )}

      {showDeleteConfirm && (
        <div className="confirmation-dialog-add-shipment-fee">
          <div className="dialog-content-add-shipment-fee">
            <p>Are you sure you want to delete this? This action will remove it from all associated products and cannot be undone.</p>
            <div className="dialog-actions-add-shipment-fee">
              <button className="cancel-btn-add-shipment-fee" onClick={() => setShowDeleteConfirm(false)}>Cancel</button>
              <button className="confirm-btn-add-shipment-fee" onClick={handleDelete}>Confirm</button>
            </div>
          </div>
        </div>
      )}

      {showErrorDialog && (
        <div className="error-dialog-add-shipment-fee">
          <div className="dialog-content-add-shipment-fee">
            {errorMessage}
            <button
              className="close-btn-add-shipment-fee"
              onClick={() => setShowErrorDialog(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      <div className="config-form-add-new-shipment">
        <button
          className={`expand-toggle-btn ${expandedForm ? 'active' : ''}`}
          onClick={handleExpandToggle}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
            <FiPlus />
            <span>Add New Shipment Configuration</span>
          </div>
          <FiChevronRight className={`expand-icon ${expandedForm ? 'rotated' : ''}`} />
        </button>

        <div className={`form-content-wrapper ${!expandedForm ? 'collapsed' : ''}`}>
          <div className="config-form-content">
            <div className="form-section-add-new-shipment">
              <div className="form-group-add-new-shipment">
                <label className="form-label-add-new-shipment">Country</label>
                <select
                  className="form-select"
                  value={selectedCountry}
                  onChange={handleCountryChange}
                >
                  <option value="">Select Country</option>
                  {countries.map((country, index) => (
                    <option key={index} value={country.countryName}>{country.countryName}</option>
                  ))}
                </select>
              </div>

              {selectedCountry && (
                <div className="form-group-add-new-shipment">
                  <label className="form-label-add-new-shipment">Port</label>
                  <select
                    className="form-select"
                    value={selectedPort}
                    onChange={handlePortChange}
                  >
                    <option value="">Select Port</option>
                    {ports.map((port, index) => (
                      <option key={index} value={port.portName}>{port.portName}</option>
                    ))}
                  </select>
                </div>
              )}

              {selectedPort && (
                <div className="form-group-add-new-shipment">
                  <label className="form-label-add-new-shipment">Shipping Option</label>
                  <div className="shipping-option-container-add-new-shipment">
                    <div className="shipping-options-display-add-new-shipment">
                      {shippingOptions.map((option, index) => (
                        <span key={index} className="shipping-option-badge">{option}</span>
                      ))}
                    </div>
                    <div className="auto-apply-notice-add-new-shipment">
                      <span className="notice-label-add-new-shipment">Both options will be applied</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {selectedPort && (
              <div className="cost-inputs-wrapper">
                <div className="form-group-add-new-shipment">
                  <label className="form-label-add-new-shipment">Base Cost (per 0-14m³)</label>
                  <input
                    type="number"
                    className="form-input"
                    value={baseCost}
                    onChange={(e) => setBaseCost(e.target.value)}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                  />
                </div>

                <div className="form-group-add-new-shipment">
                  <label className="form-label-add-new-shipment">Base Cost (per 14-20m³)</label>
                  <input
                    type="number"
                    className="form-input"
                    value={shippingAmount14To20m3}
                    onChange={(e) => setShippingAmount14To20m3(e.target.value)}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                  />
                </div>

                <div className="form-group-add-new-shipment">
                  <label className="form-label-add-new-shipment">Container Rate (20ft)</label>
                  <input
                    type="number"
                    className="form-input"
                    value={containerRate}
                    onChange={(e) => setContainerRate(e.target.value)}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                  />
                </div>

                <div className="form-group-add-new-shipment">
                  <label className="form-label-add-new-shipment">Container Rate (40ft)</label>
                  <input
                    type="number"
                    className="form-input"
                    value={containerRate40ft}
                    onChange={(e) => setContainerRate40ft(e.target.value)}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>
            )}

            {hasAnyCostValue && (
              <div className="form-actions">
                <button className="cancel-btn-add-shipment-fee" onClick={resetForm}>
                  <FiX className="action-icon" /> Cancel
                </button>
                <button className="save-btn-add-shipment-fee" onClick={handleSave}>
                  <FiCheck className="action-icon" /> Save Configuration
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="summary-tables">
        <div className="summary-table">
          <div className="summary-table-header">
            <h2 className="table-header">Shipment Charges Summary</h2>
            <div className="search-wrapper-add-shipment" style={{ marginBottom: 0 }}>
              <div className="search-input-container-add-shipment">
                <input
                  type="text"
                  className="search-input"
                  placeholder="Search..."
                  value={searchCharges}
                  onChange={(e) => setSearchCharges(e.target.value)}
                />
                {searchCharges && (
                  <button
                    className="clear-search-btn"
                    onClick={() => setSearchCharges('')}
                    title="Clear search"
                  >
                    ✕
                  </button>
                )}
              </div>
              <span className="search-result-count">{filteredCharges.length}/{charges.length}</span>
            </div>
            <button
              className="minimize-section-btn"
              onClick={() => toggleSectionMinimize('charges')}
              title={minimizedSections.charges ? 'Expand' : 'Minimize'}
            >
              {minimizedSections.charges ? '▶' : '▼'}
            </button>
          </div>
          <div className={`section-content-wrapper ${minimizedSections.charges ? 'minimized' : ''}`}>
            {filteredCharges.length > 0 ? (
              <div className="card-container-add-shipment">
                {filteredCharges.map((entry, index) => (
                  <div key={index} className={`charge-card-add-shipment ${editingId === entry.shippingOptions[0].shippingOptionValueId ? 'editing-mode' : ''}`}>
                    <div className="card-header-add-shipment">
                      <h3>{entry.portName}</h3>
                    </div>
                    <div className="card-body-add-shipment">
                      <div className="card-row-add-shipment">
                        <span className="card-label-add-shipment">Country:</span>
                        <span className="card-value-add-shipment">{entry.countryName}</span>
                      </div>

                      <div className="shipping-options-row">
                        <span className="row-label">Shipping Options:</span>
                        <div className="options-badges">
                          {entry.shippingOptions.map((option, idx) => (
                            <span key={idx} className="option-badge">{option.type}</span>
                          ))}
                        </div>
                      </div>

                      {editingId === entry.shippingOptions[0].shippingOptionValueId ? (
                        <div className="cost-details-edit-mode">
                          <div className="edit-field-group">
                            <label className="edit-field-label">0-14m³</label>
                            <input
                              type="number"
                              className="edit-field-input"
                              value={tempValues.baseCost}
                              onChange={(e) => handleFieldChange('baseCost', e.target.value)}
                              min="0"
                              step="0.01"
                              placeholder="0.00"
                            />
                          </div>
                          <div className="edit-field-group">
                            <label className="edit-field-label">14-20m³</label>
                            <input
                              type="number"
                              className="edit-field-input"
                              value={tempValues.shippingAmount14To20m3}
                              onChange={(e) => handleFieldChange('shippingAmount14To20m3', e.target.value)}
                              min="0"
                              step="0.01"
                              placeholder="0.00"
                            />
                          </div>
                          <div className="edit-field-group">
                            <label className="edit-field-label">20ft Container</label>
                            <input
                              type="number"
                              className="edit-field-input"
                              value={tempValues.containerRate}
                              onChange={(e) => handleFieldChange('containerRate', e.target.value)}
                              min="0"
                              step="0.01"
                              placeholder="0.00"
                            />
                          </div>
                          <div className="edit-field-group">
                            <label className="edit-field-label">40ft Container</label>
                            <input
                              type="number"
                              className="edit-field-input"
                              value={tempValues.containerRate40ft}
                              onChange={(e) => handleFieldChange('containerRate40ft', e.target.value)}
                              min="0"
                              step="0.01"
                              placeholder="0.00"
                            />
                          </div>
                          <div className="edit-mode-actions">
                            <button onClick={handleEditSave} className="edit-save-btn" title="Save changes">
                              <FiCheck /> Save
                            </button>
                            <button onClick={handleCancelEdit} className="edit-cancel-btn" title="Cancel editing">
                              <FiX /> Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="cost-details-compact">
                          <div className="cost-item-compact">
                            <span className="cost-label-compact">Rate(0-14m³):</span>
                            <span className="cost-value-compact">${(entry.shippingOptions[0]?.baseCost || 0).toFixed(2)}</span>
                          </div>
                          <div className="cost-item-compact">
                            <span className="cost-label-compact">Rate(14-20m³):</span>
                            <span className="cost-value-compact">${(entry.shippingOptions[0]?.shippingAmount14To20m3 || 0).toFixed(2)}</span>
                          </div>
                          <div className="cost-item-compact">
                            <span className="cost-label-compact">Container(20ft):</span>
                            <span className="cost-value-compact">${(entry.shippingOptions[0]?.containerRate || 0).toFixed(2)}</span>
                          </div>
                          <div className="cost-item-compact">
                            <span className="cost-label-compact">Container(40ft):</span>
                            <span className="cost-value-compact">${(entry.shippingOptions[0]?.containerRate40ft || 0).toFixed(2)}</span>
                          </div>
                        </div>
                      )}
                    </div>
                    {editingId !== entry.shippingOptions[0].shippingOptionValueId && (
                      <div className="card-actions-add-shipment">
                        <button
                          className="edit-btn-add-shipment-fee"
                          onClick={() => handleEditClick(entry.shippingOptions[0].shippingOptionValueId, entry.shippingOptions[0])}
                          title="Edit All Fields"
                        >
                          <FiEdit2 />
                        </button>
                        <button
                          className="delete-btn-add-shipment-fee"
                          onClick={() => confirmDelete(entry.shippingOptions[0].shippingOptionValueId)}
                          title="Delete Configuration"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : charges.length === 0 ? (
              <div className="empty-state">No shipment charges configured yet</div>
            ) : (
              <div className="no-results-state">No results found for "{searchCharges}"</div>
            )}
          </div>
        </div>

        <div className="summary-table">
          <div className="summary-table-header">
            <h2 className="table-header">Ports Without Charges</h2>
            <div className="search-wrapper-add-shipment" style={{ marginBottom: 0 }}>
              <div className="search-input-container-add-shipment">
                <input
                  type="text"
                  className="search-input"
                  placeholder="Search..."
                  value={searchPending}
                  onChange={(e) => setSearchPending(e.target.value)}
                />
                {searchPending && (
                  <button
                    className="clear-search-btn"
                    onClick={() => setSearchPending('')}
                    title="Clear search"
                  >
                    ✕
                  </button>
                )}
              </div>
              <span className="search-result-count">{filteredPending.length}/{portsWithoutCharges.length}</span>
            </div>
            <button
              className="minimize-section-btn"
              onClick={() => toggleSectionMinimize('pending')}
              title={minimizedSections.pending ? 'Expand' : 'Minimize'}
            >
              {minimizedSections.pending ? '▶' : '▼'}
            </button>
          </div>
          <div className={`section-content-wrapper ${minimizedSections.pending ? 'minimized' : ''}`}>
            {filteredPending.length > 0 ? (
              <div className="missing-ports-container">
                {filteredPending.map((port, index) => (
                  <button
                    key={index}
                    className="missing-port-button"
                    onClick={() => handlePortWithoutChargeClick(port)}
                  >
                    <div className="port-button-content">
                      <span className="port-name">{port.portName}</span>
                      <span className="country-name">{port.countryName}</span>
                    </div>
                    <div className="port-button-action">
                      <span className="configure-badge">
                        Configure
                        <svg className="arrow-icon" viewBox="0 0 24 24">
                          <path d="M5 12h14M12 5l7 7-7 7" />
                        </svg>
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            ) : portsWithoutCharges.length === 0 ? (
              <div className="empty-state">All ports have charges configured</div>
            ) : (
              <div className="no-results-state">No results found for "{searchPending}"</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddShipmentFee;